import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import type { Session, User } from '@supabase/supabase-js';
import { authClient } from '@/lib/supabase/auth';

interface Ctx {
  session: Session | null;
  user: User | null;
  loading: boolean;
  signInWithGoogle: (redirectTo?: string) => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<Ctx | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    authClient.auth.getSession().then(({ data }) => {
      setSession(data.session ?? null);
      setLoading(false);
    });
    const { data: sub } = authClient.auth.onAuthStateChange((_event, s) => {
      setSession(s);
    });
    return () => sub.subscription.unsubscribe();
  }, []);

  const value = useMemo<Ctx>(
    () => ({
      session,
      user: session?.user ?? null,
      loading,
      signInWithGoogle: async (redirectTo?: string) => {
        const dest = redirectTo ?? window.location.origin;
        await authClient.auth.signInWithOAuth({
          provider: 'google',
          options: {
            redirectTo: dest,
            queryParams: { access_type: 'offline', prompt: 'consent' },
          },
        });
      },
      signOut: async () => {
        await authClient.auth.signOut();
      },
    }),
    [session, loading],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): Ctx {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}

/** Emails allowed to trigger paid/expensive jobs (Find contacts, Run research). */
export const PRIVILEGED_EMAILS = ['gurmeet@flaxitup.com'];

/** True only for the privileged operator (currently Gurmeet). Others can view
 *  but not trigger the paid research / enrichment jobs. */
export function useIsPrivileged(): boolean {
  const { user } = useAuth();
  const email = (user?.email ?? '').toLowerCase();
  return PRIVILEGED_EMAILS.includes(email);
}
